import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BehaviorSubject, catchError, EMPTY, forkJoin, Observable, of, switchMap, takeUntil } from 'rxjs';
import { NotificationManager } from '../../../../managers/notification.manager';
import { BaseComponent } from '../../../base.component';
import { User } from '../../../../models/ui/user.model';
import { UserProfileService } from '../../../../services/user-profile.service';
import { UserManager } from '../../../../managers/user.manager';
import { UserProfile } from '../../../../models/user-profile.model';
import { AccountAdminService } from '../../../../services/account-admin.service';
import { MatSelectionListChange } from '@angular/material/list';
import { LexbeCase } from '../../../../models/case.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ExportUsersDialogComponent } from '../export-users-dialog/export-users-dialog.component';
import { ManageAccountAccessDialogComponent } from '../manage-account-access-dialog/manage-account-access-dialog.component';
import { ManageCaseAccessDialogComponent } from '../manage-case-access-dialog/manage-case-access-dialog.component';
import { ManageAccountRolesDialogComponent } from '../manage-account-roles-dialog/manage-account-roles-dialog.component';
import { UserEditDialogComponent } from './user-edit-dialog/user-edit-dialog.component';
import { OkCancelDialogComponent } from '../../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { OkCancelData } from '../../../dialogs/ok-cancel/ok-cancel-dialog.model';
import { CaseAdminSdk } from '../../../../sdks/case-admin.sdk';
import { RoleGuid } from '../../../../models/role.model';
import { ThirdPartyManager } from '@managers/third-party.manager';
import { ManageOpenAISettingDialogComponent } from '../manage-open-ai-setting-dialog/manage-open-ai-setting-dialog.component';

@Component({
    selector: 'lex-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.scss'],
    standalone: false
})
export class UserEditComponent extends BaseComponent implements OnChanges {

	@Input() selectedUserId: string = '';
	@Input() searchTerm: string = '';
	@Input() selectedOrderBy: [string, "asc" | "desc"] = ['caseName', 'asc'];

	public selectedUser: User;
	public selectedUserProfile: UserProfile;
	public selectedUserCurrentCase: string = '';
	public selectedAccountRoles: string[] = [];
	public selectedAccountCases: LexbeCase[] = [];
	public selectedCasesUploadedDoc: string[] = [];
	public roleGuid = RoleGuid;

	private selectedAccount = new BehaviorSubject<string>('');
	private selectedAccountId: string = '';

	constructor(
		private notificationManager: NotificationManager,
		private userProfileService: UserProfileService,
		private userManager: UserManager,
		private accountAdminService: AccountAdminService,
		private caseAdminSdk: CaseAdminSdk,
		private thirdPartyManager: ThirdPartyManager,
		private dialog: MatDialog) {
		super();
		this.selectedAccount.pipe(
			switchMap((accountId: string) => {
				if (accountId && this.selectedUserId) {
					accountAdminService.getAccountUserRoleIds(accountId, this.selectedUserId).subscribe(data => {
						if (data && data.length > 0)
							this.selectedAccountRoles = data;
						else
							this.selectedAccountRoles = [];
					});
					this.userManager.getUserCasesUploadedDoc(this.selectedUserId, accountId).subscribe(data => {
						this.selectedCasesUploadedDoc = data;
					});
				}
				if (accountId && this.selectedUser) {
					this.selectedAccountCases = this.selectedUser.cases.filter(x => x.accountId === accountId);
				}
				return of(null);
			}),
			takeUntil(this.destroy$)
		).subscribe();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.selectedUserId && this.selectedUserId !== this.selectedUser?.id) {
			this.selectedAccountRoles = [];
			this.selectedAccountCases = [];
			this.selectedAccountId = '';
			this.selectedAccount.next(this.selectedAccountId);
			this.updateUser().subscribe();
		}
	}

	public selectAccountIdChange(event: MatSelectionListChange): void {
		this.selectedAccountId = event.options[0].value;
		this.selectedAccount.next(this.selectedAccountId);
	}

	public exportUsers(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = this.selectedUser;
		this.dialog.open(ExportUsersDialogComponent, dialogConfig).afterClosed().subscribe();
	}

	public editUser(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = { selectedUser: this.selectedUser, selectedUserProfile: this.selectedUserProfile };
		this.dialog.open(UserEditDialogComponent, dialogConfig).afterClosed()
			.pipe(switchMap((success) => {
				if (success) {
					this.userManager.refreshAdminUsers(this.searchTerm, this.selectedOrderBy);
					this.notificationManager.displaySnackBarMessage({
						message: 'User updated successfully.'
					});
				}
				return of(null);
			}), switchMap(() => this.updateUser()))
			.subscribe();
	}

	public manageOpenAI(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = this.selectedUserId;
		this.dialog.open(ManageOpenAISettingDialogComponent, dialogConfig)
			.afterClosed()
			.subscribe();
	}

	public deleteUser(): void {
		this.deleteUserDialog().subscribe(() => {
			this.userManager.refreshAdminUsers(this.searchTerm, this.selectedOrderBy);
			this.notificationManager.displaySnackBarMessage({
				message: 'User deleted successfully.'
			});
		}, err => {
			this.notificationManager.displaySnackBarMessage({
				message: `Failed to delete user- ${err}`,
				type: 'error'
			});
		});
	}

	public manageAccountAccess(): void {
		this.manageAccountAccessDialog()
			.pipe(switchMap(() => {
				this.notificationManager.displaySnackBarMessage({
					message: 'Account access for user updated successfully.'
				});
				return this.updateUser();
			}), catchError(err => {
				this.notificationManager.displaySnackBarMessage({
					message: `Failed to update user's account access- ${err}`,
					type: 'error'
				});
				return EMPTY;
			})).subscribe();
	}

	public manageAccountRoles(): void {
		this.manageAccountRolesDialog()
			.pipe(switchMap(() => {
				this.notificationManager.displaySnackBarMessage({
					message: 'Account roles for user updated successfully.'
				});
				return this.updateUser();
			}), catchError(err => {
				this.notificationManager.displaySnackBarMessage({
					message: `Failed to update user's account roles- ${err}`,
					type: 'error'
				});
				return EMPTY;
			})).subscribe();
	}

	public manageCaseAccess(): void {
		this.manageCaseAccessDialog()
			.pipe(switchMap(() => {
				this.notificationManager.displaySnackBarMessage({
					message: 'Case access for user updated successfully.'
				});
				return this.updateUser();
			}), catchError(err => {
				this.notificationManager.displaySnackBarMessage({
					message: `Failed to update user's case access- ${err}`,
					type: 'error'
				});
				return EMPTY;
			})).subscribe();
	}

	//#region Dialogs for Admin Users specific functionalities

	private deleteUserDialog(): Observable<void> {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';

		dialogConfig.data = {
			title: `Delete User Confirmation`,
			message: `The user will no longer have access.`,
			showAcknowledge: true
		} as OkCancelData;

		return this.dialog.open(OkCancelDialogComponent, dialogConfig)
			.afterClosed()
			.pipe(
				switchMap((confirm) => {
					if (confirm)
						return this.userManager.deleteUser(this.selectedUser.id);
					return EMPTY;
				})
			);
	}

	private manageAccountAccessDialog(): Observable<string> {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.minWidth = '500px';
		dialogConfig.maxWidth = '750px';
		dialogConfig.data = this.selectedUser;
		return this.dialog.open(ManageAccountAccessDialogComponent, dialogConfig).afterClosed()
			.pipe(switchMap((accountIds: string[]) => {
				if (accountIds)
					return this.accountAdminService.updateUserAccountAccess(this.selectedUserId, accountIds);
				return EMPTY;
			}));
	}

	private manageAccountRolesDialog(): Observable<string> {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.minWidth = '500px';
		dialogConfig.maxWidth = '750px';
		dialogConfig.data = { selectedUser: this.selectedUser, selectedAccountId: this.selectedAccountId, selectedAccountRoles: this.selectedAccountRoles };
		return this.dialog.open(ManageAccountRolesDialogComponent, dialogConfig).afterClosed()
			.pipe(
				switchMap((roleIds) => {
					if (roleIds)
						return this.accountAdminService.updateUserAccountRoles(this.selectedUserId, this.selectedAccountId, roleIds);
					return EMPTY;
				}));
	}

	private manageCaseAccessDialog(): Observable<string> {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.minWidth = '500px';
		dialogConfig.maxWidth = '750px';
		dialogConfig.data = { selectedUser: this.selectedUser, selectedAccountId: this.selectedAccountId };
		return this.dialog.open(ManageCaseAccessDialogComponent, dialogConfig).afterClosed()
			.pipe(switchMap((caseIds) => {
				if (caseIds)
					return this.caseAdminSdk.updateUserCasesAccesses(this.selectedUserId, caseIds);
				return EMPTY;
			}));
	}

	//#endregion Dialogs for Admin Users specific functionalities

	private updateUser(): Observable<void> {
		return forkJoin(
			this.userProfileService.getUserProfile(this.selectedUserId),
			this.userManager.getUserDetails(this.selectedUserId),
			this.userManager.getUserCurrentCase(this.selectedUserId),
		).pipe(switchMap(data => {
			this.selectedUserProfile = data[0];
			this.selectedUser = data[1];
			this.selectedUserCurrentCase = data[2];
			if (this.selectedUser.accounts.length > 0) {
				if (!this.selectedAccountId)
					this.selectedAccountId = this.selectedUser.accounts[0].accountId;
				this.selectedAccount.next(this.selectedAccountId);
			}
			return of(null);
		}));
	}
}
